/**
 * api.js — TransferExpo CRM API Service
 * Wired to the dedicated CRM server (transferexpocrmserver).
 * Base URL is read from REACT_APP_API_URL.
 * In production, an empty base keeps requests on the CRM domain so nginx can
 * proxy /api/* to the CRM server.
 */

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ─── core fetch wrapper ───────────────────────────────────────────────────────

async function request(method, path, body, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = data?.message || `HTTP ${res.status}`;
    throw Object.assign(new Error(msg), { status: res.status, data });
  }

  return data;
}

async function requestMultipart(method, path, body, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body,
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = data?.message || `HTTP ${res.status}`;
    throw Object.assign(new Error(msg), { status: res.status, data });
  }

  return data;
}

const get   = (path, token)       => request('GET',    path, null, token);
const post  = (path, body, token) => request('POST',   path, body, token);
const patch = (path, body, token) => request('PATCH',  path, body, token);
const put   = (path, body, token) => request('PUT',    path, body, token);
const del   = (path, token)       => request('DELETE', path, null, token);

// ─── query string helper ──────────────────────────────────────────────────────

function qs(params = {}) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────────────────

export const adminLogin  = (email, password) => post('/api/auth/login',  { email, password });
export const adminLogout = (token)           => post('/api/auth/logout', {}, token);
export const getAdminProfile      = (token)              => get('/api/auth/me', token);
export const updateAdminPassword  = (token, currentPassword, newPassword) =>
  post('/api/auth/change-password', { currentPassword, newPassword }, token);

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

export const getDashboardStats    = (token) => get('/api/dashboard/stats',    token);
export const getDashboardCharts   = (token) => get('/api/dashboard/stats',    token); // same endpoint
export const getRevenueAnalytics  = (token, period = 'month') => get(`/api/dashboard/revenue?period=${period}`, token);
export const getCrmKpis           = (token) => get('/api/dashboard/stats',    token);
export const getCrmActivity       = (token) => get('/api/dashboard/activity', token);
export const getAppStats          = (token) => get('/api/dashboard/app-stats', token);

// ─────────────────────────────────────────────────────────────────────────────
// PLAYERS
// ─────────────────────────────────────────────────────────────────────────────

export const getPlayers       = (token, params = {}) => get(`/api/players${qs(params)}`, token);
export const getPlayerById    = (token, id)          => get(`/api/players/${id}`, token);
export const createPlayer     = (token, body)        => post('/api/players', body, token);
export const updatePlayer     = (token, id, body)    => put(`/api/players/${id}`, body, token);
export const updatePlayerStatus = (token, id, isActive) =>
  patch(`/api/players/${id}/status`, { isActive }, token);
export const deletePlayer     = (token, id)          => del(`/api/players/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// SCOUTS
// ─────────────────────────────────────────────────────────────────────────────

export const getScouts        = (token, params = {}) => get(`/api/scouts${qs(params)}`, token);
export const getScoutById     = (token, id)          => get(`/api/scouts/${id}`, token);
export const createScout      = (token, body)        => post('/api/scouts', body, token);
export const updateScout      = (token, id, body)    => put(`/api/scouts/${id}`, body, token);
export const updateScoutStatus = (token, id, isActive) =>
  patch(`/api/scouts/${id}/status`, { isActive }, token);
export const deleteScout      = (token, id)          => del(`/api/scouts/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// AGENTS
// ─────────────────────────────────────────────────────────────────────────────

export const getAgents        = (token, params = {}) => get(`/api/agents${qs(params)}`, token);
export const getAgentById     = (token, id)          => get(`/api/agents/${id}`, token);
export const createAgent      = (token, body)        => post('/api/agents', body, token);
export const updateAgent      = (token, id, body)    => put(`/api/agents/${id}`, body, token);
export const updateAgentStatus = (token, id, isActive) =>
  patch(`/api/agents/${id}/status`, { isActive }, token);
export const suspendAgent     = (token, id, reason)  => patch(`/api/agents/${id}/suspend`, { reason }, token);
export const deleteAgent      = (token, id)          => del(`/api/agents/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// CLUBS
// ─────────────────────────────────────────────────────────────────────────────

export const getClubs         = (token, params = {}) => get(`/api/clubs${qs(params)}`, token);
export const getClubById      = (token, id)          => get(`/api/clubs/${id}`, token);
export const createClub       = (token, body)        => post('/api/clubs', body, token);
export const updateClub       = (token, id, body)    => put(`/api/clubs/${id}`, body, token);
export const updateClubVerification = (token, id, status, note) =>
  patch(`/api/clubs/${id}/status`, { status, note }, token);
export const suspendClub      = (token, id, reason)  =>
  patch(`/api/clubs/${id}/status`, { status: 'suspended', reason }, token);
export const deleteClub       = (token, id)          => del(`/api/clubs/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS
// ─────────────────────────────────────────────────────────────────────────────

export const getEvents        = (token, params = {}) => get(`/api/events${qs(params)}`, token);
export const getEventById     = (token, id)          => get(`/api/events/${id}`, token);
export const createEvent      = (token, body)        => post('/api/events', body, token);
export const updateEvent      = (token, id, body)    => put(`/api/events/${id}`, body, token);
export const updateEventStatus = (token, id, status) =>
  patch(`/api/events/${id}/status`, { status }, token);
export const deleteEvent      = (token, id)          => del(`/api/events/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENTS
// ─────────────────────────────────────────────────────────────────────────────

export const getPayments      = (token, params = {}) => get(`/api/payments${qs(params)}`, token);
export const getPaymentById   = (token, id)          => get(`/api/payments/${id}`, token);
export const getPaymentSummary = (token)             => get('/api/payments/summary', token);
export const refundPayment    = (token, id, reason)  =>
  patch(`/api/payments/${id}/status`, { status: 'refunded', reason }, token);
export const getRevenueAnalyticsV2 = (token)         => get('/api/payments/summary', token);

// ─────────────────────────────────────────────────────────────────────────────
// PAYOUTS (mapped to payments for now — no separate payouts module)
// ─────────────────────────────────────────────────────────────────────────────

export const getPayoutStats   = (token)              => get('/api/dashboard/stats', token);
export const getPayouts       = (token, params = {}) => get(`/api/payments${qs(params)}`, token);
export const getPayoutById    = (token, id)          => get(`/api/payments/${id}`, token);
export const updatePayoutStatus = (token, id, status, adminNote) =>
  patch(`/api/payments/${id}/status`, { status, adminNote }, token);

// ─────────────────────────────────────────────────────────────────────────────
// TRIAL APPLICATIONS
// ─────────────────────────────────────────────────────────────────────────────

export const getTrialApplications = (token, params = {}) =>
  get(`/api/trial-applications${qs(params)}`, token);
export const approveTrialApplication = (token, id) =>
  patch(`/api/trial-applications/${id}/approve`, {}, token);
export const rejectTrialApplication  = (token, id) =>
  patch(`/api/trial-applications/${id}/reject`, {}, token);

// ─────────────────────────────────────────────────────────────────────────────
// AUDIT TRAIL
// ─────────────────────────────────────────────────────────────────────────────

export const getAuditTrails   = (token, params = {}) => get(`/api/audit-trail${qs(params)}`, token);
export const getAuditTrailById = (token, id)         => get(`/api/audit-trail/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// USER MANAGEMENT (admin users)
// ─────────────────────────────────────────────────────────────────────────────

export const getUsers         = (token, params = {}) => get(`/api/users${qs(params)}`, token);
export const getUserById      = (token, id)          => get(`/api/users/${id}`, token);
export const createUser       = (token, body)        => post('/api/users', body, token);
export const updateUser       = (token, id, body)    => put(`/api/users/${id}`, body, token);
export const updateUserStatus = (token, id, isActive) =>
  patch(`/api/users/${id}/status`, { isActive }, token);
export const deleteUser       = (token, id)          => del(`/api/users/${id}`, token);
export const searchUsers      = (token, params = {}) => get(`/api/users${qs(params)}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// CONTACTS
// ─────────────────────────────────────────────────────────────────────────────

export const getCrmContacts   = (token, params = {}) => get(`/api/contacts${qs(params)}`, token);
export const getContactById   = (token, id)          => get(`/api/contacts/${id}`, token);
export const createContact    = (token, body)        => post('/api/contacts', body, token);
export const updateContact    = (token, id, body)    => put(`/api/contacts/${id}`, body, token);
export const deleteContact    = (token, id)          => del(`/api/contacts/${id}`, token);
export const updateCrmStage   = (token, id, type, stage) =>
  put(`/api/contacts/${id}`, { stage }, token);

// ─────────────────────────────────────────────────────────────────────────────
// PIPELINE (uses contacts with stage filter)
// ─────────────────────────────────────────────────────────────────────────────

export const getCrmPipeline   = (token, type) =>
  get(`/api/contacts${qs({ type, grouped: true })}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// TASKS
// ─────────────────────────────────────────────────────────────────────────────

export const getTasks         = (token, params = {}) => get(`/api/tasks${qs(params)}`, token);
export const createTask       = (token, body)        => post('/api/tasks', body, token);
export const updateTask       = (token, id, body)    => put(`/api/tasks/${id}`, body, token);
export const deleteTask       = (token, id)          => del(`/api/tasks/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// WORKFLOWS
// ─────────────────────────────────────────────────────────────────────────────

export const getWorkflows     = (token)              => get('/api/workflows', token);
export const createWorkflow   = (token, body)        => post('/api/workflows', body, token);
export const updateWorkflow   = (token, id, body)    => patch(`/api/workflows/${id}/status`, body, token);
export const deleteWorkflow   = (token, id)          => del(`/api/workflows/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// DEALS
// ─────────────────────────────────────────────────────────────────────────────

export const getDeals         = (token, params = {}) => get(`/api/deals${qs(params)}`, token);
export const createDeal       = (token, body)        => post('/api/deals', body, token);
export const updateDeal       = (token, id, body)    => put(`/api/deals/${id}`, body, token);
export const updateDealStage  = (token, id, stage)   =>
  patch(`/api/deals/${id}/stage`, { stage }, token);
export const deleteDeal       = (token, id)          => del(`/api/deals/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// MEMBERSHIP
// ─────────────────────────────────────────────────────────────────────────────

export const getMembershipPlans   = (token)          => get('/api/membership', token);
export const updateMembershipPlan = (token, id, body) => put(`/api/membership/${id}`, body, token);

// ─────────────────────────────────────────────────────────────────────────────
// DEVELOPER — API Keys & Webhooks
// ─────────────────────────────────────────────────────────────────────────────

export const getApiKeys       = (token)              => get('/api/developer/keys', token);
export const createApiKey     = (token, body)        => post('/api/developer/keys', body, token);
export const revokeApiKey     = (token, id)          => patch(`/api/developer/keys/${id}/revoke`, {}, token);
export const deleteApiKey     = (token, id)          => del(`/api/developer/keys/${id}`, token);

export const getWebhooks      = (token)              => get('/api/developer/webhooks', token);
export const createWebhook    = (token, body)        => post('/api/developer/webhooks', body, token);
export const updateWebhook    = (token, id, body)    => put(`/api/developer/webhooks/${id}`, body, token);
export const deleteWebhook    = (token, id)          => del(`/api/developer/webhooks/${id}`, token);

// ─────────────────────────────────────────────────────────────────────────────
// APP PROXY — real mobile app data via /api/app/*
// ─────────────────────────────────────────────────────────────────────────────

export const getAppPlayers        = (token, params = {}) => get(`/api/app/players${qs(params)}`, token);
export const getAppPlayerById     = (token, id)          => get(`/api/app/players/${id}`, token);
export const getAppPlayerFootballJourney = (token, id)   => get(`/api/app/players/${id}/football-journey`, token);
export const getAppPlayerStats = (token, id)             => get(`/api/app/players/${id}/stats`, token);
export const updateAppPlayerStatus= (token, id, isActive, reason) => patch(`/api/app/players/${id}/status`, { isActive, reason }, token);
export const onboardAppPlayer = (token, formData)        => requestMultipart('POST', '/api/app/players/onboard', formData, token);
export const createAppPlayerRequest = (token, body)      => post('/api/app/player-requests', body, token);

export const getAppScouts         = (token, params = {}) => get(`/api/app/scouts${qs(params)}`, token);
export const getAppScoutById      = (token, id)          => get(`/api/app/scouts/${id}`, token);
export const updateAppScoutStatus = (token, id, isActive, reason) => patch(`/api/app/scouts/${id}/status`, { isActive, reason }, token);

export const getAppAgents         = (token, params = {}) => get(`/api/app/agents${qs(params)}`, token);
export const getAppAgentById      = (token, id)          => get(`/api/app/agents/${id}`, token);
export const updateAppAgentStatus = (token, id, isActive, reason) => patch(`/api/app/agents/${id}/status`, { isActive, reason }, token);

export const getAppClubs          = (token, params = {}) => get(`/api/app/clubs${qs(params)}`, token);
export const getAppClubById       = (token, id)          => get(`/api/app/clubs/${id}`, token);
export const verifyAppClub        = (token, id, status, note) => patch(`/api/app/clubs/${id}/verification`, { status, note }, token);
export const suspendAppClub       = (token, id, reason)  => patch(`/api/app/clubs/${id}/suspend`, { reason }, token);

export const getAppOrganizers     = (token, params = {}) => get(`/api/app/organizers${qs(params)}`, token);
export const getAppCommunities    = (token, params = {}) => get(`/api/app/communities${qs(params)}`, token);
export const reassignAppCommunity = (token, id, organizerId) => patch(`/api/app/communities/${id}/reassign`, { organizerId }, token);
export const getAppCommunityDetails = (token, id) => get(`/api/app/communities/${id}`, token);
export const updateAppCommunity = (token, id, body) => body instanceof FormData
  ? requestMultipart('PATCH', `/api/app/communities/${id}`, body, token)
  : patch(`/api/app/communities/${id}`, body, token);
export const decideAppCommunityJoinRequest = (token, id, requestId, decision, notes = '') =>
  patch(`/api/app/communities/${id}/join-requests/${requestId}`, { decision, notes }, token);
export const sendAppCommunityInvites = (token, id, emails, message = '') =>
  post(`/api/app/communities/${id}/invites`, { emails, message }, token);
export const getAppOrganizerById  = (token, id)          => get(`/api/app/organizers/${id}`, token);
export const updateAppOrganizerStatus = (token, id, body) => patch(`/api/app/organizers/${id}/status`, body, token);
export const createAppOrganizerCommunity = (token, id, formData) => requestMultipart('POST', `/api/app/organizers/${id}/communities`, formData, token);
export const getAppOrganizerMatches = (token, id)        => get(`/api/app/organizers/${id}/matches`, token);
export const getAppOrganizerPitches = (token, id)        => get(`/api/app/organizers/${id}/pitches`, token);
export const getAppOrganizerTransactions = (token, id, params = {}) =>
  get(`/api/app/organizers/${id}/transactions${qs(params)}`, token);
export const topUpAppOrganizerWallet = (token, id, body) => post(`/api/app/organizers/${id}/wallet/top-up`, body, token);

export const getAppMarketplaceSummary = (token) => get('/api/app/marketplace/summary', token);
export const searchAppMarketplacePlayers = (token, params = {}) => get(`/api/app/marketplace/players${qs(params)}`, token);
export const getAppMarketplaceMatchRequests = (token, params = {}) => get(`/api/app/marketplace/match-requests${qs(params)}`, token);
export const getAppMarketplaceMatches = (token, params = {}) => get(`/api/app/marketplace/matches${qs(params)}`, token);
export const getAppMarketplacePitches = (token, params = {}) => get(`/api/app/marketplace/pitches${qs(params)}`, token);

export const getAppEvents         = (token, params = {}) => get(`/api/app/events${qs(params)}`, token);
export const updateAppEventStatus = (token, id, status, reason) => patch(`/api/app/events/${id}/status`, { status, reason }, token);
export const deleteAppEvent       = (token, id)          => del(`/api/app/events/${id}`, token);

export const getAppPayments       = (token, params = {}) => get(`/api/app/payments${qs(params)}`, token);
export const getAppPaymentAnalytics = (token)            => get('/api/app/payments/analytics', token);
export const refundAppPayment     = (token, id, reason)  => patch(`/api/app/payments/${id}/refund`, { reason }, token);

export const getAppPayouts        = (token, params = {}) => get(`/api/app/payouts${qs(params)}`, token);
export const getAppPayoutStats    = (token)              => get('/api/app/payouts/stats', token);
export const updateAppPayoutStatus= (token, id, status, adminNote) => patch(`/api/app/payouts/${id}/status`, { status, adminNote }, token);

export const getSequenceEnrollments = (token, seqId, params={}) => get(`/api/sequences/${seqId}/enrollments${qs(params)}`, token);
export const getAppTrialApplications = (token, params = {}) => get(`/api/app/trial-applications${qs(params)}`, token);
export const getAppAuditTrails    = (token, params = {}) => get(`/api/app/audit-trails${qs(params)}`, token);
export const getAppUsers          = (token, params = {}) => get(`/api/app/users${qs(params)}`, token);
export const updateAppUserStatus  = (token, id, isActive, reason) => patch(`/api/app/users/${id}/status`, { isActive, reason }, token);
